import cn from 'classnames';
import {
  cloneElement,
  type ForwardedRef,
  forwardRef,
  type HTMLProps,
  type MemoExoticComponent,
  memo,
  type ReactElement,
  type ReactNode,
} from 'react';

import { TYPOGRAPHY_VARIANTS } from '../../../config/typography/constants';
import { ICON_SIZES } from '../icon/constants';
import { LINK_BUTTON_VARIANTS } from './constants';
import * as styles from './LinkButton.styles';

export type LinkButtonProps = Omit<HTMLProps<HTMLButtonElement>, 'type' | 'ref'> & {
  /** Button text. */
  children: ReactNode;
  /** Icon (right side of the text) from IconPark. */
  icon?: ReactElement;
  /** Color variant. */
  variant?: LINK_BUTTON_VARIANTS;
};

export const LinkButtonBase = forwardRef(
  (
    {
      disabled = false,
      children,
      icon = null,
      className = null,
      variant = LINK_BUTTON_VARIANTS.PRIMARY,
      ...props
    }: LinkButtonProps,
    ref: ForwardedRef<HTMLButtonElement>,
  ) => (
    <button
      {...props}
      ref={ref}
      type="button"
      className={cn(className, variant, { disabled })}
      css={[styles.button, (theme) => theme.typography[TYPOGRAPHY_VARIANTS.BODY_MEDIUM_REGULAR]]}
      disabled={disabled}
    >
      <span>{children}</span>
      {icon &&
        cloneElement(icon, {
          size: ICON_SIZES.LARGE,
        })}
    </button>
  ),
);

LinkButtonBase.displayName = 'LinkButton';

type CompoundedComponent = MemoExoticComponent<typeof LinkButtonBase> & {
  VARIANTS: typeof LINK_BUTTON_VARIANTS;
};

const LinkButton = memo(LinkButtonBase) as CompoundedComponent;

LinkButton.VARIANTS = LINK_BUTTON_VARIANTS;

export { LinkButton };
